import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import { createPortal } from "react-dom";
import logo from "../../assets/images/variacao_estilizada.png";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Menu, X } from "lucide-react";
import MiniCartDropdown from "./MiniCartDropdown";
import { selectCarrinhoPorCanal } from "../../redux/cartReducer";
import { trackEvent } from "../../analytics/ga";


function useIsMobile(breakpointPx = 1024) {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(`(max-width:${breakpointPx - 1}px)`).matches
      : false
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${breakpointPx - 1}px)`);
    const handler = (e) => setIsMobile(e.matches);

    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);

    setIsMobile(mq.matches);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, [breakpointPx]);

  return isMobile;
}

export default function HeaderHortifruti() {
  const sacola = useSelector((state) => selectCarrinhoPorCanal(state, "hortifruti"));
  const totalItens = sacola.reduce((acc, item) => acc + Number(item.quantity || 1), 0);

  const [menuOpen, setMenuOpen] = useState(false);
  const [showCart, setShowCart] = useState(false);

  const headerRef = useRef(null);
  const anchorBtnRef = useRef(null);
  const dropdownRef = useRef(null);

  const [mobileTop, setMobileTop] = useState(88);
  const [anchorPos, setAnchorPos] = useState({ top: 0, right: 16 });

  const [categorias, setCategorias] = useState([]);
  const isMobile = useIsMobile(1024);

  const toggleMenu = () => setMenuOpen((v) => !v);
  const closeMenu = () => setMenuOpen(false);

  // ✅ calcula top do dropdown no mobile baseado na altura real do header
  useEffect(() => {
    const calcTop = () => {
      const h = headerRef.current?.getBoundingClientRect()?.height || 72;
      setMobileTop(Math.round(h + 12)); // +12 de respiro
    };

    calcTop();
    window.addEventListener("resize", calcTop);
    window.addEventListener("scroll", calcTop, { passive: true });

    return () => {
      window.removeEventListener("resize", calcTop);
      window.removeEventListener("scroll", calcTop);
    };
  }, []);

  // ✅ Carrega categorias do banco
  useEffect(() => {
  const url = "https://congolinaria.com.br/api/categorias_hortifruti.php";


  axios
    .get(url, {
      timeout: 15000,
      headers: { Accept: "application/json" },
      validateStatus: () => true, // não estoura exception só por status
    })
    .then((res) => {
      const ct = String(res.headers?.["content-type"] || "").toLowerCase();

      // ✅ se veio HTML, fallback
      if (!ct.includes("application/json")) {
        console.warn("Categorias: resposta não é JSON:", ct);
        setCategorias([
          { id: 1, nome: "Legumes" },
          { id: 2, nome: "Verduras" },
          { id: 6, nome: "Frutas" },
          { id: 3, nome: "Temperos" },
          { id: 4, nome: "Cogumelos" },
          { id: 5, nome: "Castanhas e Grãos" },
        ]);
        return;
      }

      const data = res.data;

      const arr =
        Array.isArray(data) ? data :
        Array.isArray(data?.data) ? data.data :
        Array.isArray(data?.categorias) ? data.categorias :
        Array.isArray(data?.result) ? data.result :
        [];

      const norm = arr
        .map((c) => {
          const id = Number(c.id ?? c.categoria_id ?? 0);
          const nome = String(
            c.nome_pt ?? c.nome ?? c.titulo ?? c.categoria ?? c.nome_categoria ?? ""
          ).trim();
          const ordem = Number(c.ordem ?? 0);
          const slug = String(c.slug ?? "").trim();
          return { id, nome, slug, ordem };
        })
        .filter((c) => c.id > 0 && c.nome);

      if (!norm.length) {
        setCategorias([
          { id: 1, nome: "Legumes" },
          { id: 2, nome: "Verduras" },
          { id: 6, nome: "Frutas" },
          { id: 3, nome: "Temperos" },
          { id: 4, nome: "Cogumelos" },
          { id: 5, nome: "Castanhas e Grãos" },
        ]);
        return;
      }

      norm.sort((a, b) => (a.ordem || 999) - (b.ordem || 999) || a.nome.localeCompare(b.nome, "pt-BR"));
      setCategorias(norm);
    })
    .catch((err) => {
      console.log("Falha ao carregar categorias:", err?.message || err);
      setCategorias([
        { id: 1, nome: "Legumes" },
        { id: 2, nome: "Verduras" },
        { id: 6, nome: "Frutas" },
        { id: 3, nome: "Temperos" },
        { id: 4, nome: "Cogumelos" },
        { id: 5, nome: "Castanhas e Grãos" },
      ]);
    });
}, []);



  const navLinks = useMemo(() => {
    const links = [{ to: "/hortifruti", label: "Início" }];
    categorias.forEach((c) => links.push({ to: `/hortifruti?cat=${c.id}`, label: c.nome }));
    return links;
  }, [categorias]);

  // ✅ quando abrir a sacola no desktop, captura posição do botão
  useEffect(() => {
    if (!showCart) return;

    if (isMobile) {
      setMenuOpen(false);
      return;
    }

    const el = anchorBtnRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    setAnchorPos({
      top: rect.bottom + 12,
      right: Math.max(16, window.innerWidth - rect.right),
    });
  }, [showCart, isMobile]);

  // ✅ fecha ao clicar fora (portal)
  useEffect(() => {
    if (!showCart) return;

    const onDown = (e) => {
      const inAnchor = anchorBtnRef.current?.contains(e.target);
      const inDrop = dropdownRef.current?.contains(e.target);
      if (!inAnchor && !inDrop) setShowCart(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [showCart]);

  // ✅ ESC fecha
  useEffect(() => {
    if (!showCart) return;
    const onKey = (e) => e.key === "Escape" && setShowCart(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showCart]);

  // ✅ trava scroll no mobile com sacola aberta
  useEffect(() => {
    if (!isMobile || !showCart) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile, showCart]);

  // ✅ Fecha sacola se abrir menu
  useEffect(() => {
    if (menuOpen) setShowCart(false);
  }, [menuOpen]);

  const CartPortal = () => {
    if (!showCart) return null;
    if (typeof document === "undefined") return null;

    return createPortal(
      <AnimatePresence>
        {showCart && (
          <>
            {/* Backdrop */}
            <motion.button
              type="button"
              aria-label="Fechar sacola"
              onClick={() => setShowCart(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9998] bg-black/40"
            />

            {/* Painel */}
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className={
                isMobile
                  ? "fixed z-[9999] left-4 right-4 max-h-[75vh] overflow-auto rounded-2xl shadow-2xl"
                  : "fixed z-[9999] w-[360px] max-w-[calc(100vw-32px)] rounded-2xl shadow-2xl"
              }
              style={
                isMobile
                  ? { top: mobileTop }
                  : { top: `${anchorPos.top}px`, right: `${anchorPos.right}px` }
              }
            >
              <MiniCartDropdown canal="hortifruti" onClose={() => setShowCart(false)} />
            </motion.div>
          </>
        )}
      </AnimatePresence>,
      document.body
    );
  };

  return (
    <>
      <header ref={headerRef} className="sticky top-0 z-50 bg-[#144D3A] shadow-md">
  <div className="max-w-7xl mx-auto flex items-center justify-between px-4 py-3">
    {/* Logo */}
    <Link to="/hortifruti" className="flex items-center gap-2 shrink-0">
      <img
        src={logo}
        alt="Hortifruti Congolinaria"
        className="w-16 h-12 object-contain"
      />
    </Link>

    {/* Nav desktop */}
    <nav className="hidden lg:flex gap-6 text-sm font-semibold whitespace-nowrap overflow-x-auto">
      {navLinks.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.to === "/hortifruti"}
          className={({ isActive }) =>
            `font-medium transition ${
              isActive ? "text-yellow-300" : "text-white"
            } hover:text-yellow-300`
          }
        >
          {item.label}
        </NavLink>
      ))}
    </nav>

    {/* Ações: sacola + menu mobile */}
    <div className="flex items-center gap-3 sm:gap-4 relative shrink-0">
      <button
        ref={anchorBtnRef}
        onClick={() => {
          setShowCart((v) => !v);
          trackEvent("view_cart", { channel: "hortifruti", place: "header" });
        }}
        className="relative flex items-center justify-center w-11 h-11 rounded-full
          text-white hover:text-[#C9A23F] hover:bg-white/10 transition"
        aria-label="Abrir sacola"
        type="button"
      >
        <ShoppingBag size={28} color="white" />
        {totalItens > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px]
              bg-yellow-500 text-white text-[11px] font-bold
              flex items-center justify-center rounded-full px-1"
          >
            {totalItens}
          </span>
        )}
      </button>

      <button
        className="lg:hidden flex items-center justify-center w-11 h-11 rounded-full
          text-white hover:bg-white/10 focus:outline-none transition"
        onClick={toggleMenu}
        aria-label="Abrir menu"
        type="button"
      >
        {menuOpen ? <X size={30} /> : <Menu size={30} />}
      </button>
    </div>
  </div>
</header>


      {/* Portal da sacola */}
      <CartPortal />

      {/* Menu mobile verde */}
      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden bg-[#0f3a2a]/95 backdrop-blur-md shadow-md px-5 py-5 flex flex-col gap-4 text-white text-base font-medium"
          >
            {navLinks.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/hortifruti"}
                onClick={closeMenu}
                className={({ isActive }) =>
                  `transition hover:text-[#C9A23F] ${
                    isActive ? "text-[#C9A23F]" : "text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
}
